import React from 'react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { WorkflowStatusBadge } from 'src/app/workflows/WorkflowStatusBadge';

export interface IWorkflowStatusStep {
  buttonColor?: string | null;
}

export interface IWorkflowStatusRow {
  status?: string | null;
  stepIndex?: number | null;
  workflow?: {
    steps?: (IWorkflowStatusStep | null | undefined)[] | null;
  } | null;
}

/**
 * @registry Resolves a workflow row's active-step button color: the `buttonColor` of
 * `workflow.steps[stepIndex ?? 0]`. Returns undefined when no step applies.
 */
export const resolveStepColor = (
  row: IWorkflowStatusRow,
): string | null | undefined =>
  row.workflow?.steps?.[row.stepIndex ?? 0]?.buttonColor;

export interface IWorkflowStatusColumnOptions<T> {
  size?: number;
  /** Extra color used when the active step has none, e.g. the RFI status palette. */
  colorFallback?: (row: T) => string | null | undefined;
}

/**
 * @registry Status column factory for workflow tables: renders `<WorkflowStatusBadge>`
 * colored from the row's active step (via resolveStepColor), with an optional per-row
 * color fallback. Use in any MantineReactTable whose row carries status/stepIndex/workflow.
 */
export const workflowStatusColumn = <T extends IWorkflowStatusRow>(
  options: IWorkflowStatusColumnOptions<T> = {},
): MRT_ColumnDef<T> => ({
  accessorKey: 'status',
  header: 'Status',
  ...(options.size != null ? { size: options.size } : {}),
  Cell: ({ row }) => (
    <WorkflowStatusBadge
      status={row.original.status}
      buttonColor={
        resolveStepColor(row.original) ?? options.colorFallback?.(row.original)
      }
    />
  ),
});
